import { Component, inject, Input, OnDestroy } from "@angular/core";
import mapboxgl from "mapbox-gl";
import { bbox, centroid } from "@turf/turf"; // Import the bbox function from turf
import { UserFieldService } from "../../../services/user-field/user-field.service";
import { Subject, Subscription } from "rxjs";
import { throttleTime } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { Role } from "../../utils/data-util";
import { LoadingService } from "../../../services/loading/loading";

@Component({
  selector: "app-dashboard-map",
  standalone: true,
  imports: [],
  templateUrl: "./dashboard-map.html",
  styleUrl: "./dashboard-map.scss",
})
export class DashboardMap implements OnDestroy {
  // Input are the geom (id, field_id, geom and entity of claimedFields) and mode
  @Input() geom?: any;
  @Input() mode?: string | null;
  @Input() type!: Role;
  // Initializing map, markers, styles, minBounds and geoJsonData
  map!: mapboxgl.Map;
  minBounds: any;
  geoJsonData: any;
  private markers: any[] = [];
  // selected id of geojson feature
  selectedId: any;
  // Grower Variable - selectedEntity
  selectedEntity: string = "all entities";
  // Manager Variable - selectedUserName
  selectedUserName: string = "all users";
  // Subscriptions
  private subscriptions = new Subscription();
  currentBaseMap: "normal" | "satellite" = "satellite";
  private loadingService = inject(LoadingService);
  private resizeObserver?: ResizeObserver;

  // Constructor
  constructor(private userFieldService: UserFieldService) {}

  // Function for switching the map
  switchBasemap(target: "normal" | "satellite"): void {
    if (!this.map) return;
    this.currentBaseMap = target;
    if (target === "satellite") {
      this.map.setStyle("mapbox://styles/mapbox/satellite-streets-v12");
      return;
    }
    // Default basemap follows mode setting
    const normalStyle =
      this.mode === "dark" ?
        "mapbox://styles/mapbox/dark-v11"
      : "mapbox://styles/mapbox/light-v10";
    this.map.setStyle(normalStyle);
  }

  // Generate GeoJson based on the input geom
  generateGeoJson() {
    const features = this.geom?.map((item: any) => {
      const displayName = (
        item.username ||
        [item.first_name, item.last_name].filter(Boolean).join(" ")
      )?.toLowerCase();
      const entityNames: string[] =
        Array.isArray(item.entities) ?
          item.entities
            .map((e: { name: string; email: string }) => e?.name?.toLowerCase())
            .filter((n: string | undefined): n is string => !!n)
        : [];
      const feature = {
        type: "Feature",
        id: item.id,
        properties: {
          id: item.id,
          field_id: item.field_id,
          entities: entityNames,
          username: displayName,
        },
        geometry: item.geom,
      };
      return feature;
    });
    return {
      type: "FeatureCollection",
      features: features,
    };
  }

  // Function of zooming to the mark
  zoomToMarker(coordinates: [number, number]): void {
    this.map.jumpTo({
      center: coordinates,
      zoom: 14,
    });
  }

  // Adding markers based on the centroid point of the geojson polygon
  addMarkers(): void {
    this.geoJsonData.features.map((feature: any) => {
      // Get coordinates of centroidPoint
      const centroidPoint = centroid(feature);
      const coordinates = centroidPoint.geometry.coordinates as any;
      // Create marker object
      const marker = (new mapboxgl.Marker({ color: "#7E3AF2" }) as any)
        .setLngLat(coordinates)
        .addTo(this.map);
      // Add ZoomTo click event
      marker
        .getElement()
        .addEventListener("click", () => this.zoomToMarker(coordinates));
      // Set two properties for the maker object,
      // Push the marker to the array, should control the visibility later.
      marker["id"] = feature.id;
      if (this.type === "grower") {
        marker["entities"] = feature.properties.entities;
      } else if (this.type === "manager" || this.type === "entity") {
        marker["username"] = feature.properties.username;
      }
      this.markers.push(marker);
    });
  }

  // Function to control visibility of markers
  setMarkersVisibility(visibility: "visible" | "none"): void {
    this.markers.forEach((marker) => {
      const element = marker.getElement();
      if (element) {
        //element.style.display = visibility === 'visible' ? 'block' : 'none';
        if (visibility === "visible") {
          if (this.type === "grower") {
            const markerEntities: string[] =
              Array.isArray(marker.entities) ? marker.entities : [];
            if (
              this.selectedEntity === "all entities" ||
              markerEntities.includes(this.selectedEntity)
            ) {
              element.style.display = "block";
            } else {
              element.style.display = "none";
            }
          } else if (this.type === "manager" || this.type === "entity") {
            if (
              this.selectedUserName === "all users" ||
              marker.username?.toLowerCase() === this.selectedUserName
            ) {
              element.style.display = "block";
            } else {
              element.style.display = "none";
            }
          }
        } else {
          element.style.display = "none";
        }
      }
    });
  }

  // Functions triggered when clicking a certain feature of GeoJson Layer
  onPolygonClick(e: any): void {
    // Get the id of the selected feature
    const id = e.features[0].id;
    // Reset the previously selected feature's state, selected -> false
    if (this.selectedId) {
      this.map.setFeatureState(
        { source: "geojson-data", id: this.selectedId },
        { selected: false },
      );
    }
    // Set the new feature's state, selected -> true
    this.map.setFeatureState(
      { source: "geojson-data", id: id },
      { selected: true },
    );
    this.selectedId = id;
    this.userFieldService.setSelectedUserFieldId(this.selectedId);
  }

  // This function is for initializing the map
  initMap(): void {
    // Start loading indicator for initial map setup
    this.loadingService.start({
      token: "dashboard-map-moving",
      message: "Loading map",
    });

    // Initialize the map
    this.map = new mapboxgl.Map({
      container: "map", // The ID of the container element
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [-119.41052448611953, 35.6997473719842],
      zoom: 7,
      minZoom: 7,
      maxZoom: 14,
      accessToken: environment.mapboxPublickAccessToken,
    });
    // Observe parent element for size changes and resize the map, throttled to once per 100ms
    const resizeSubject = new Subject<void>();
    this.subscriptions.add(
      resizeSubject.pipe(throttleTime(100)).subscribe(() => this.map?.resize()),
    );
    const mapContainer = this.map.getContainer();
    const parent = mapContainer.parentElement;
    if (parent) {
      this.resizeObserver = new ResizeObserver(() => resizeSubject.next());
      this.resizeObserver.observe(parent);
    }
    // Load and implement some inital settings
    this.map.on("load", () => {
      const nav = new mapboxgl.NavigationControl();
      this.map.addControl(nav, "top-left");
      this.map.getCanvas().style.cursor = "pointer";
    });
    // Stop loading when map is completely idle (movement stopped, tiles loaded, styles loaded)
    this.map.on("idle", () => {
      this.loadingService.stop("dashboard-map-moving");
    });
    // All case will be using pointer cursor
    this.map.on("mousemove", (e) => {
      this.map.getCanvas().style.cursor = "pointer";
    });
    // Change cursor back to default when leaving the polygon layer
    this.map.on("mouseleave", "geojson-layer", () => {
      this.map.getCanvas().style.cursor = "";
    });
  }

  // Function for updating Map Settings
  addMapElements(): void {
    // Generate the GeoJson based on geom
    this.geoJsonData = this.generateGeoJson();
    // Get the current selectedFeature
    const selectedFeature =
      this.selectedId ?
        this.geoJsonData.features.find(
          (item: { id: any }) => item.id === this.selectedId,
        )
      : this.geoJsonData.features[0];
    // Calculate the bounding box of the first feature of geoJsonData and set the state of minBounds
    const bounds = bbox(selectedFeature);
    this.minBounds = [
      [bounds[0], bounds[1]],
      [bounds[2], bounds[3]],
    ];
    // Adding GeoJson Layer (source & layer)
    this.map.addSource("geojson-data", {
      type: "geojson",
      data: this.geoJsonData,
    });
    this.map.addLayer({
      id: "geojson-layer",
      type: "fill",
      source: "geojson-data",
      layout: {},
      paint: {
        "fill-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#2563eb", // Color when selected
          "#7E3AF2", // Default color
        ],
        "fill-opacity": 0.8,
        "fill-outline-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#FACA15", // Border color when selected (black)
          "#7E3AF2", // Border color when not selected (transparent or same as default)
        ],
      },
    });
    this.map.addLayer({
      id: "geojson-outline",
      type: "line",
      source: "geojson-data",
      paint: {
        "line-color": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          "#FACA15",
          "#7E3AF2",
        ],
        "line-width": [
          "case",
          ["boolean", ["feature-state", "selected"], false],
          3, // thicker when selected
          1, // thin default
        ],
      },
    });
    // Adding Markers based on geojson features
    this.addMarkers();
    const zoomLevel = this.map.getZoom();
    if (zoomLevel && zoomLevel < 11) {
      // when zoom level < 12, geojson-layer, geojson-outline not show, only shows markers.
      this.map.setLayoutProperty("geojson-layer", "visibility", "none");
      this.map.setLayoutProperty("geojson-outline", "visibility", "none");
      this.setMarkersVisibility("visible");
    } else {
      // when zoom level >= 12, geojson-layer, geojson-outline shows but markers not show.
      this.map.setLayoutProperty("geojson-layer", "visibility", "visible");
      this.map.setLayoutProperty("geojson-outline", "visibility", "visible");
      this.setMarkersVisibility("none");
    }
    // Control the visibility of geojson-layer, geojson-outline and marker when zoom in or out
    this.map.on("zoom", () => {
      const zoomLevel = this.map.getZoom();
      if (zoomLevel && zoomLevel < 11) {
        // when zoom level < 12, geojson-layer, geojson-outline not show, only shows markers.
        this.map.setLayoutProperty("geojson-layer", "visibility", "none");
        this.map.setLayoutProperty("geojson-outline", "visibility", "none");
        this.setMarkersVisibility("visible");
      } else {
        // when zoom level >= 12, geojson-layer, geojson-outline shows but markers not show.
        this.map.setLayoutProperty("geojson-layer", "visibility", "visible");
        this.map.setLayoutProperty("geojson-outline", "visibility", "visible");
        this.setMarkersVisibility("none");
      }
    });
    // Add click event listener
    this.map.on("click", "geojson-layer", (e) => this.onPolygonClick(e));
    // Fit the map to the bounds of the GeoJSON data
    this.map.fitBounds(this.minBounds, {
      padding: 75,
      animate: false,
    });
    // Set the new feature's state, selected -> true
    this.map.setFeatureState(
      { source: "geojson-data", id: selectedFeature.id },
      { selected: true },
    );
    // continue using the selectedFeature to set the selectedUserFieldId
    this.userFieldService.setSelectedUserFieldId(selectedFeature.id);
    // If the mode is dark, appending gray styles to map look nicer :)
    if (this.mode === "dark" && this.currentBaseMap === "normal") {
      this.map.setPaintProperty("landuse", "fill-color", "#374151");
      this.map.setPaintProperty("land", "background-color", "#1f2937");
    }
  }

  // Function for reset map when data changes (mainly mode)
  resetMap() {
    // Step 1: Remove the existing map instance
    this.resizeObserver?.disconnect();
    if (this.map) {
      this.map.remove();
    }
    // Step 2: Re-initialize the map
    this.initMap();
    // Step 3: Wait until the style is fully loaded before adding sources or layers
    this.map.on("style.load", () => {
      this.addMapElements();
    });
    // Step 4: initialize the currentBaseMap as normal
    this.currentBaseMap = "satellite";
  }

  // ngOnChanges Licycle Hook
  ngOnChanges(): void {
    // Wait until the geom is initialized.
    if (this.geom && this.mode) {
      // When geom or mode update, call resetMap
      this.resetMap();
    }
  }

  // ngOnInit Licycle Hook
  ngOnInit(): void {
    if (!this.map) {
      this.initMap();
    }
    // Subscribe to the selectedUserFieldId observable
    this.subscriptions.add(
      this.userFieldService.selectedUserFieldId$.subscribe((id) => {
        // Only update the Map Settings when id, geoJsonData, markers and map are not null
        if (id && this.geoJsonData && this.markers && this.map) {
          // Zoom to selected feature's marker
          const markerLatLong = this.markers.find(
            (item) => item.id === id,
          )?._lngLat;
          if (markerLatLong) {
            this.zoomToMarker([markerLatLong.lng, markerLatLong.lat]);
          }
          // Reset the previously selected feature's state, selected -> false
          if (this.selectedId) {
            this.map.setFeatureState(
              { source: "geojson-data", id: this.selectedId },
              { selected: false },
            );
          }
          // Set the new feature's state, selected -> true
          this.map.setFeatureState(
            { source: "geojson-data", id: id },
            { selected: true },
          );
          // Update the current selectedId
          this.selectedId = id;
        }
      }),
    );

    if (this.type === "grower") {
      // Subscribe to the selectedGrowerSubject observable
      this.subscriptions.add(
        this.userFieldService.selectedEntity$.subscribe((entityName) => {
          // Only update the Map Settings when growerName, geoJsonData, markers and map are not null
          if (entityName && this.geoJsonData && this.markers && this.map) {
            this.selectedEntity = entityName;
            // Filter Geom
            this.filterGeomByEntityName();
            // Filter Marker based on the current zoom
            const zoomLevel = this.map.getZoom();
            zoomLevel && zoomLevel < 11 ?
              this.setMarkersVisibility("visible")
            : this.setMarkersVisibility("none");
          }
        }),
      );
    } else if (this.type === "manager" || this.type === "entity") {
      // Subscribe to the selectedGrowerSubject observable
      this.subscriptions.add(
        this.userFieldService.selectedGrower$.subscribe((growerName) => {
          // Only update the Map Settings when growerName, geoJsonData, markers and map are not null
          if (growerName && this.geoJsonData && this.markers && this.map) {
            this.selectedUserName = growerName;
            // Filter Geom
            this.filterGeomByGrowerName();
            // Filter Marker based on the current zoom
            const zoomLevel = this.map.getZoom();
            zoomLevel && zoomLevel < 11 ?
              this.setMarkersVisibility("visible")
            : this.setMarkersVisibility("none");
          }
        }),
      );
    }
  }

  // Make the map null when lifecycle ends
  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.subscriptions.unsubscribe();
    if (this.map) {
      this.map.remove();
    }
    this.loadingService.stop("dashboard-map-moving");
  }

  // Function to set filter on geom based on entity name (matches any entity in the field's entities array)
  filterGeomByEntityName(): void {
    if (this.selectedEntity !== "all entities") {
      this.map.setFilter("geojson-layer", [
        "in",
        this.selectedEntity,
        ["get", "entities"],
      ]);
    } else {
      this.map.setFilter("geojson-layer", null);
    }
  }

  // Function to set filter on geom based on username (growername)
  filterGeomByGrowerName(): void {
    // Filter the geojson
    if (this.selectedUserName !== "all users") {
      this.map.setFilter("geojson-layer", [
        "==",
        ["get", "username"],
        this.selectedUserName,
      ]);
    } else {
      this.map.setFilter("geojson-layer", null);
    }
  }
}
